/**
 * Agent Improvement Conversation — boundary contract schemas (improvement-agent DC-1).
 *
 * Owned here: `apps/api/src/routes/manager/improvement-conversation.routes.ts` registers these
 * schemas, `apps/api/src/routes/admin/admin-agent-feedback.routes.ts` registers the admin transcript
 * view, and the landlord and admin frontends derive their types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   POST /landlord/api/improvement-conversations                                   (start)
 *   POST /landlord/api/improvement-conversations/:id/messages                      (message)
 *   GET  /landlord/api/improvement-conversations/:id                               (read)
 *   POST /landlord/api/improvement-conversations/:id/proposals/:proposalId/confirm
 *   POST /landlord/api/improvement-conversations/:id/proposals/:proposalId/not-quite
 *   POST /landlord/api/improvement-conversations/:id/close
 *   POST /landlord/api/improvement-conversations/:id/reroute
 *   GET  /admin/api/agent-feedback/:id/conversation                                (admin transcript)
 *   POST /agent/api/improvement-conversations/:conversationId/turns/:turnId        (runtime turn completion, DC-3)
 *
 * The runtime owns the conversation and its transcript (IMP-BR-2); every view here is a relay of
 * the runtime's response in camelCase. The request schemas carry no landlord or user id: identity
 * comes only from the session (BR-CONV-RELAY-IDENT).
 */
import { z } from 'zod';
import { playbookAgentKindSchema, ruleScopeSchema } from './playbook.js';
/** Max trimmed length of a landlord turn (contract ImprovementStartRequest / MessageRequest). */
export const IMPROVEMENT_TEXT_MAX = 2000;
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const improvementEntrySchema = z.enum(['message', 'draft', 'tell_us_why', 'case', 'rules_page']);
export const improvementStateSchema = z.enum(['awaiting_landlord', 'turn_running', 'proposal_pending', 'closed']);
export const improvementOutcomeCategorySchema = z.enum([
    'preference_applied',
    'defect_escalated',
    'explained',
    'declined',
    'abandoned',
]);
export const improvementRerouteTargetSchema = z.enum(['preference', 'defect']);
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
export const improvementTextSchema = z.string().trim().min(1).max(IMPROVEMENT_TEXT_MAX);
export const improvementStartAnchorSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('message'), caseEventId: z.uuid() }),
    z.object({ kind: z.literal('draft'), reviewLogId: z.uuid() }),
    z.object({ kind: z.literal('none'), caseId: z.uuid().optional(), agentKind: playbookAgentKindSchema.optional() }),
]);
export const improvementStartBodySchema = z.object({
    entry: improvementEntrySchema,
    anchor: improvementStartAnchorSchema,
    caseId: z.uuid().optional(),
    text: improvementTextSchema,
});
export const improvementMessageBodySchema = z.object({ text: improvementTextSchema });
export const improvementRerouteBodySchema = z.object({ to: improvementRerouteTargetSchema });
/** The landlord's choice on a shown conflict (BR-12); Narrow is a landlord message, not a confirm. */
export const improvementConflictChoiceSchema = z.enum(['replace', 'keep_both']);
/** Confirm body (DC-1): the body is optional; an absent `choice` means keep_both. */
export const improvementConfirmBodySchema = z.object({ choice: improvementConflictChoiceSchema.optional() });
export const improvementConversationParamsSchema = z.object({ id: z.uuid() });
export const improvementProposalParamsSchema = z.object({ id: z.uuid(), proposalId: z.string().min(1).max(200) });
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
/** 202: the turn is running; the reply arrives by the realtime notice, then a read. */
export const improvementTurnAcceptedSchema = z.object({ conversationId: z.uuid(), turnId: z.string().min(1) });
/** 200: a start that matched an existing open conversation for the same anchor. */
export const improvementExistingConversationSchema = z.object({ conversationId: z.uuid(), existing: z.literal(true) });
export const improvementStartResponseSchema = z.union([
    improvementTurnAcceptedSchema,
    improvementExistingConversationSchema,
]);
export const improvementMessageResponseSchema = z.object({ turnId: z.string().min(1) });
export const improvementTurnSchema = z.object({
    turnId: z.string(),
    role: z.enum(['landlord', 'agent']),
    text: z.string(),
    at: z.string().describe('ISO 8601'),
});
export const improvementConflictSchema = z.object({
    item: z.object({
        kind: z.enum(['rule', 'default']),
        id: z.string(),
        scope: ruleScopeSchema,
        text: z.string(),
    }),
    choices: z.array(z.enum(['replace', 'narrow', 'keep_both'])),
});
export const improvementProposalSchema = z.object({
    proposalId: z.string(),
    agentKind: playbookAgentKindSchema,
    scope: ruleScopeSchema,
    operations: z.array(z.object({
        op: z.string(),
        ruleId: z.string().optional(),
        itemId: z.string().optional(),
        text: z.string().optional(),
    })),
    summary: z.string(),
    conflict: improvementConflictSchema.optional(),
    baseVersionId: z.string().min(1).max(200).nullable(),
});
export const improvementConversationViewSchema = z.object({
    conversationId: z.uuid(),
    state: improvementStateSchema,
    outcome: improvementOutcomeCategorySchema.optional(),
    reroutedFrom: improvementOutcomeCategorySchema.optional(),
    anchor: z.object({
        kind: z.enum(['message', 'draft', 'none']),
        caseId: z.uuid().optional(),
        agentKind: playbookAgentKindSchema,
    }),
    turns: z.array(improvementTurnSchema),
    proposal: improvementProposalSchema.optional(),
    conflict: improvementConflictSchema.optional(),
    budget: z.object({ messagesLeft: z.number().int().nonnegative() }),
    reroute: z.enum(['to_preference', 'to_defect']).optional(),
});
export const improvementConfirmResponseSchema = z.object({
    agentKind: playbookAgentKindSchema,
    scope: ruleScopeSchema,
    versionId: z.string().min(1).max(200),
});
/** 409 from the runtime's state machine / a stale proposal. */
export const improvementConflictResponseSchema = z.object({ error: z.string() });
/** 429: over the landlord's per-window turn limit (BR-CONV-RATE). */
export const improvementRateLimitedResponseSchema = z.object({
    error: z.literal('rate_limited'),
    retryAfterSeconds: z.number().int().nonnegative(),
});
/** 503: the agent runtime is unreachable, slow or failing (BR-CONV-RELAY-DOWN). */
export const improvementRuntimeUnavailableResponseSchema = z.object({ error: z.literal('agent_runtime_unavailable') });
/** 502: the agent runtime answered with a body that violates its contract (a producer defect, not an outage). */
export const improvementRuntimeBadResponseSchema = z.object({ error: z.literal('agent_runtime_bad_response') });
/** 422: a runtime validation failure, or a refused defect. */
export const improvementValidationErrorResponseSchema = z.object({
    errors: z.array(z.object({ path: z.string(), message: z.string() })),
});
// ---------------------------------------------------------------------------
// Turn completion (DC-3) — the runtime's callback; mirrors the `TurnCompletion` def of
// packages/agent-tools/contracts/agent-runtime-control.schema.json (snake_case, no reply text)
// ---------------------------------------------------------------------------
const defectActionSchema = z.strictObject({
    capability: z
        .enum([
        'message_tenant',
        'message_vendor',
        'message_landlord',
        'assign_vendor',
        'schedule_appointment',
        'reschedule_appointment',
        'follow_up',
        'escalate_to_landlord',
        'categorize',
        'close_case',
        'reply_to_prospect',
        'propose_showing',
        'send_application',
        'close_lead',
    ])
        .optional(),
    text: z.string().optional(),
});
// The contract's turn_id is a uuid (ImprovementAccepted / ImprovementTurn), and it is stored in
// AgentFeedback.conversationTurnId (uuid, unique): anything else is refused here, not at the database.
export const turnCompletionParamsSchema = z.object({ conversationId: z.uuid(), turnId: z.uuid() });
export const turnCompletionBodySchema = z.strictObject({
    landlord_id: z.uuid(),
    user_id: z.uuid(),
    status: z.enum(['completed', 'failed']),
    state: improvementStateSchema,
    outcome: z
        .strictObject({
        category: improvementOutcomeCategorySchema,
        rerouted_from: improvementOutcomeCategorySchema.optional(),
    })
        .optional(),
    defect: z
        .strictObject({
        case_id: z.uuid(),
        agent_kind: playbookAgentKindSchema,
        behavior_kind: z.enum([
            'omission',
            'commission',
            'sequencing',
            'content',
            'tone',
            'escalation',
            'ignored_playbook',
            'other',
        ]),
        action_x: defectActionSchema,
        action_y: defectActionSchema.optional(),
        anchor: z.strictObject({ kind: z.enum(['point', 'case']), ref: z.uuid().optional() }),
        expected_behavior: z.string().min(1),
        diagnosis: z.string(),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// Admin transcript (OBS-BR-49)
// ---------------------------------------------------------------------------
export const adminConversationTranscriptSchema = z.object({
    conversationId: z.uuid(),
    turns: z.array(improvementTurnSchema),
});
/**
 * The two explicit non-error states of the admin read (OBS-BR-49): a runtime 404 is `expired`; the
 * runtime unreachable, past its timeout or answering 5xx is `unavailable`. Both answer 200 so the
 * admin view can show the state while the item's other fields render.
 */
export const adminConversationStateSchema = z.object({ state: z.enum(['expired', 'unavailable']) });
/** 200 of the admin read: the relayed transcript, or an explicit expired / unavailable state (OBS-BR-49). */
export const adminConversationResponseSchema = z.union([adminConversationTranscriptSchema, adminConversationStateSchema]);
//# sourceMappingURL=improvement.js.map